import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Icon } from '../../../ui/icon/Icon';
import Button, { ButtonThemes } from '../../../ui/button/Button';
import { getClassNames } from '../../../helpers/classHelpers';
import HelpCaseCollapsedTrigger from './HelpCaseCollapsedTrigger';
import styles from './HelpCasePanel.module.scss';
import useLabels from '../../../hooks/useLabels';
import HelpdeskCaseModal from './HelpdeskCaseModal';
import HelpDeskSuccessDialog from './HelpDeskSuccessDialog';
import { SvgIcons } from '../../../ui/icon/icons/svg';
import { CreateHelpdeskCaseResponse } from '../../../services/helpdeskService';

export interface HelpCasePanelProps {
    chatTitle?: string;
    isVisible: boolean;
    isCollapsed: boolean;
    isModalOpen: boolean;
    onCloseModal: () => void;
    onCreateCase: () => void;
    onCaseCreated?: () => void;
    onDismiss: () => void;
    onExpand: () => void;
    className?: string;
}

const HelpCasePanel: FC<HelpCasePanelProps> = ({
    chatTitle = '',
    isVisible,
    isCollapsed,
    isModalOpen,
    onCloseModal,
    onCreateCase,
    onCaseCreated,
    onDismiss,
    onExpand,
    className,
}) => {
    const labels = useLabels();
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [createdCaseNumber, setCreatedCaseNumber] = useState<string>();

    const handleSuccessDialogClose = useCallback(() => setIsSuccessDialogOpen(false), []);

    const handleModalSuccess = useCallback((response?: CreateHelpdeskCaseResponse) => {
        // Persist the last successful case number in component state so the dialog can deep-link to the created case.
        setCreatedCaseNumber(response?.caseNumber ?? undefined);
        setIsSuccessDialogOpen(true);
        onCaseCreated?.();
    }, [onCaseCreated]);

    if (!isVisible) return null;

    return (
        <div className={getClassNames([styles['help-case-panel'], className])}>
            {isCollapsed ? (
                <HelpCaseCollapsedTrigger onExpand={onExpand} />
            ) : (
                <div className={getClassNames(['df-card', styles['panel']])}>
                    <div className={styles['icon-wrapper']}>
                        <Icon.Svg title={labels.help} iconName={SvgIcons.Helpdesk} className={styles.icon} />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.heading}>{labels.HelpCasePanelTitle}</div>
                        <p className={styles.description}>
                            {labels.HelpCasePanelDescription}
                        </p>
                        <div className={styles.actions}>
                                            <Button theme={ButtonThemes.primary} onClick={onCreateCase} isSmall>
                                {labels.HelpCasePanelCreateCase}
                            </Button>
                            <Button theme={ButtonThemes.secondary} onClick={onDismiss} isSmall>
                                {labels.HelpCasePanelNoThanks}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <HelpdeskCaseModal
                chatTitle={chatTitle}
                isOpen={isModalOpen}
                onClose={onCloseModal}
                onSuccess={handleModalSuccess}
            />
            <HelpDeskSuccessDialog isOpen={isSuccessDialogOpen} onClose={handleSuccessDialogClose} caseNumber={createdCaseNumber} />
        </div>
    );
};

export default HelpCasePanel;
